import { formatNumber } from '@/lib/format'
import type { ExpenseLine } from '@/types/domain'

/* The pure helpers the Money section owns: how a variance is toned, and how a
 * delta is written so it does not read as an amount. Nothing here touches React
 * or the wire, which is what makes the section's arithmetic readable in one
 * screen instead of scattered through the markup. */

/** `null` closed, `'new'` adding, a record editing — see DirectoriesPage. */
export type EditTarget<T> = T | 'new' | null

export function editing<T>(target: EditTarget<T>): T | undefined {
  return target === null || target === 'new' ? undefined : target
}

/** A NEW actual being entered against a plan that awaits it (S-4.1
 *  follow-ups): not an edit of the plan — the sheet stays "new", with no
 *  Delete — but saved as the plan's own PUT, carrying its served plan. */
export interface ExpenseLink {
  kind: 'link'
  plan: ExpenseLine
}

/** The plans a new actual may be entered against: no actual yet, not an
 *  unplanned line, and not a plan another line DETAILS (`detailOf`) — the
 *  server refuses an actual on that one (`ErrPlanIsDetailed`), because its
 *  trucks' own rate lines carry them, and those are offered instead. */
export function awaitingPlans(expenses: readonly ExpenseLine[]): ExpenseLine[] {
  const detailed = new Set(
    expenses.flatMap((line) => (line.detailOf === undefined ? [] : [line.detailOf])),
  )
  return expenses.filter(
    (line) => !line.unplanned && line.actual === undefined && !detailed.has(line.id),
  )
}

/** The tone of money spent beyond the plan. Its own name because an UNPLANNED
 *  line wears it whatever its served variance says: every unit of a cost
 *  nobody planned is overrun (S-4.1). */
export const OVERRUN_TONE = 'text-negative font-semibold'

/** Sign decides tone (AD-9 — the NUMBER is served): over plan argues in red,
 *  under plan relaxes in green, on plan stays quiet. */
export function varianceTone(amount: string): string {
  const over = Number(amount)
  return over > 0 ? OVERRUN_TONE : over < 0 ? 'text-positive' : 'text-text-muted'
}

/** `+640.00` — an explicit plus, because a bare variance figure in a money
 *  table reads as an amount, not a delta. Minus arrives from formatNumber. */
export function signedNumber(amount: string): string {
  return Number(amount) > 0 ? `+${formatNumber(amount, 2)}` : formatNumber(amount, 2)
}
